import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { FaRupeeSign, FaShoppingBag, FaPercentage, FaChartLine } from 'react-icons/fa';

const FranchiseEarnings = ({ franchiseId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEarnings = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await apiClient.get('/franchise/my-earnings', {
                    params: franchiseId ? { franchiseId } : {},
                    headers: { Authorization: `Bearer ${token}` }
                });
                setData(response.data);
            } catch (error) {
                console.error('Failed to fetch franchise earnings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchEarnings();
    }, [franchiseId]);

    if (loading) return <div className="p-8 text-center bg-white rounded-lg shadow">Loading earnings...</div>;
    if (!data) return <p className="text-red-500">Could not load earnings data.</p>;

    const maxTrendRevenue = Math.max(1, ...data.dailyTrend.map(d => d.revenue));

    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-green-100 text-green-600 flex items-center justify-center"><FaRupeeSign /></div>
                    <div>
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Total Revenue</p>
                        <p className="text-xl font-black text-gray-800">₹{data.totalRevenue.toLocaleString('en-IN')}</p>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center"><FaShoppingBag /></div>
                    <div>
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Orders</p>
                        <p className="text-xl font-black text-gray-800">{data.orderCount}</p>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center"><FaPercentage /></div>
                    <div>
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Commission Earned ({data.commissionRate}%)</p>
                        <p className="text-xl font-black text-gray-800">₹{data.commissionEarned.toLocaleString('en-IN')}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <h3 className="text-sm font-black text-gray-700 uppercase tracking-widest mb-5 flex items-center gap-2">
                    <FaChartLine className="text-emerald-500" /> Last 14 Days Revenue
                </h3>
                {data.dailyTrend.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No paid orders in the last 14 days.</p>
                ) : (
                    <div className="space-y-2">
                        {data.dailyTrend.map((d, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <span className="text-[11px] font-bold text-gray-400 w-14 shrink-0">{d._id.day} {monthNames[d._id.month]}</span>
                                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                                    <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${Math.max(4, (d.revenue / maxTrendRevenue) * 100)}%` }}></div>
                                </div>
                                <span className="text-xs font-bold text-gray-700 w-20 text-right">₹{d.revenue.toLocaleString('en-IN')}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <h3 className="text-sm font-black text-gray-700 uppercase tracking-widest mb-4">Top Selling Products</h3>
                {data.topProducts.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No sales data yet.</p>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {data.topProducts.map((p, i) => (
                            <div key={p._id || i} className="py-2.5 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                                    <p className="text-[11px] text-gray-400">{p.qty} units sold</p>
                                </div>
                                <p className="text-sm font-bold text-green-600">₹{p.revenue.toLocaleString('en-IN')}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FranchiseEarnings;
